import { Link } from "react-router-dom";
import { Star, MapPin, Clock, Heart, ArrowUpRight } from "lucide-react";

const Rating = ({ value }) => (
    <div className="flex items-center gap-1 text-sm">
        <Star size={14} className="fill-terracotta text-terracotta" />
        <span className="font-medium text-ink">{Number(value || 0).toFixed(1)}</span>
    </div>
);

const SaveButton = ({ onSave, testId }) => (
    <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSave?.(); }}
        data-testid={testId}
        className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 backdrop-blur transition hover:scale-110"
        aria-label="Save"
    >
        <Heart size={16} className="text-terracotta" />
    </button>
);

const placeHref = (type, destination, name) =>
    `/place?destination=${encodeURIComponent(destination || "")}&type=${type}&name=${encodeURIComponent(name)}`;

export const HotelCard = ({ hotel, onSave, index = 0, destination = "" }) => (
    <Link
        to={placeHref("hotel", destination, hotel.name)}
        state={{ item: hotel }}
        data-testid={`hotel-card-${index}`}
        className="group relative block overflow-hidden rounded-3xl bg-white ring-1 ring-jade/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
    >
        <div className="relative aspect-[4/3] overflow-hidden">
            <img src={hotel.image} alt={hotel.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <SaveButton onSave={onSave} testId={`hotel-save-${index}`} />
            <span className="absolute left-3 top-3 rounded-full bg-ink/80 px-3 py-1 text-xs text-sand">{"★".repeat(hotel.stars || 4)}</span>
        </div>
        <div className="p-5">
            <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-2xl leading-tight">{hotel.name}</h3>
                <Rating value={hotel.rating} />
            </div>
            <p className="mt-1 flex items-center gap-1 text-xs text-moss"><MapPin size={12} /> {hotel.neighborhood}</p>
            <p className="mt-3 text-sm text-moss/80 line-clamp-2">{hotel.highlight}</p>
            <div className="mt-4 flex flex-wrap gap-1.5">
                {(hotel.amenities || []).slice(0, 3).map((a, i) => (
                    <span key={i} className="rounded-full bg-jade/8 px-2.5 py-0.5 text-[11px] text-jade">{a}</span>
                ))}
            </div>
            <div className="mt-5 flex items-end justify-between border-t border-jade/10 pt-4">
                <div>
                    <p className="text-xs text-moss">From</p>
                    <p className="font-display text-2xl text-ink">${hotel.price_per_night_usd}<span className="text-sm text-moss font-sans">/night</span></p>
                </div>
                <span className="pill bg-ink text-sand px-4 py-2 text-xs">Details <ArrowUpRight size={12} className="ml-1" /></span>
            </div>
        </div>
    </Link>
);

export const RestaurantCard = ({ restaurant, onSave, index = 0, destination = "" }) => (
    <Link
        to={placeHref("restaurant", destination, restaurant.name)}
        state={{ item: restaurant }}
        data-testid={`restaurant-card-${index}`}
        className="group relative block overflow-hidden rounded-3xl bg-white ring-1 ring-jade/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
    >
        <div className="relative aspect-[4/3] overflow-hidden">
            <img src={restaurant.image} alt={restaurant.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <SaveButton onSave={onSave} testId={`restaurant-save-${index}`} />
            <span className="absolute left-3 top-3 rounded-full bg-terracotta/90 px-3 py-1 text-xs text-sand">{restaurant.price_range}</span>
        </div>
        <div className="p-5">
            <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-2xl leading-tight">{restaurant.name}</h3>
                <Rating value={restaurant.rating} />
            </div>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-terracotta">{restaurant.cuisine}</p>
            <p className="mt-3 text-sm text-moss/80"><span className="font-medium text-ink">Must try: </span>{restaurant.must_try}</p>
            <p className="mt-1 flex items-center gap-1 text-xs text-moss"><MapPin size={12} /> {restaurant.neighborhood}</p>
        </div>
    </Link>
);

export const ActivityCard = ({ activity, onSave, index = 0, destination = "" }) => (
    <Link
        to={placeHref("activity", destination, activity.name)}
        state={{ item: activity }}
        data-testid={`activity-card-${index}`}
        className="group relative block overflow-hidden rounded-3xl bg-white ring-1 ring-jade/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
    >
        <div className="relative aspect-[4/3] overflow-hidden">
            <img src={activity.image} alt={activity.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <SaveButton onSave={onSave} testId={`activity-save-${index}`} />
            <span className="absolute left-3 top-3 rounded-full bg-jade/90 px-3 py-1 text-xs uppercase tracking-wider text-sand">{activity.category}</span>
        </div>
        <div className="p-5">
            <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-2xl leading-tight">{activity.name}</h3>
                <Rating value={activity.rating} />
            </div>
            <p className="mt-3 text-sm text-moss/80 line-clamp-2">{activity.description}</p>
            <div className="mt-4 flex items-center justify-between border-t border-jade/10 pt-4 text-sm">
                <span className="flex items-center gap-1 text-moss"><Clock size={14} /> {activity.duration_hours}h</span>
                <span className="font-display text-xl text-ink">${activity.price_usd}</span>
            </div>
        </div>
    </Link>
);
